import {
  getElementStats,
  getElementStatsByIdentifier,
} from "core-integration/src/store/element-stats/reducers";
import { IElementStat } from "core-integration/src/store/element-stats/types";
import { getElementTypesBySquadPosition } from "core-integration/src/store/element-types/reducers";
import { getFutureEvents } from "core-integration/src/store/events/reducers";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import {
  listTableBody,
  listTableElementColumn,
  listTableHeader,
  listTablePosition,
  listTablePositionCell,
} from "plos/src/styles/listTables.css";
import { fixedTable, scrollContainer } from "plos/src/styles/utils.css";
import { getStartingAndBenchPlayers } from "plos/src/utils/getStartingAndBenchPlayers";
import { positionsByElementType } from "plos/src/utils/positionsByElementType";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useOverlayContext } from "../../../contexts/OverlayContext";
import {
  fdrEventCount,
  useOverlayColumns,
} from "../../../hooks/useOverlayColumns";
import { OverlayHeaderCells } from "../../ListViewTable/OverlayCells";
import MyTeamTableRow from "./MyTeamTableRow";
import { MyTeamTableProps } from "./types";

const MyTeamTable = ({
  picks,
  elementsById,
  chipInPlayName,
  handleElementClick,
  renderDreamTeam,
}: MyTeamTableProps) => {
  const { t } = useTranslation("pickTeam");

  const { selectedOverlay } = useOverlayContext();
  const teamsById = useSelector(getTeamsById);
  const stats = useSelector(getElementStats);
  const statsByName: Record<string, IElementStat> = useSelector(
    getElementStatsByIdentifier,
  );
  const fdrEvents = useSelector(getFutureEvents).slice(0, fdrEventCount);

  const elementTypesByPosition = useSelector(getElementTypesBySquadPosition);

  const { benchPlayers, playersByElementType } = getStartingAndBenchPlayers(
    picks,
    elementsById,
    chipInPlayName,
  );

  const columns = useOverlayColumns(selectedOverlay, {
    stats,
    statsByName,
    fdrEvents,
  });

  return (
    <div className={scrollContainer}>
      <table
        aria-label={t("myTeamTable.pickTeamList", "Pick Team List")!}
        className={fixedTable}
      >
        <thead>
          <tr>
            <th scope="col" className={listTableElementColumn}>
              <div className={listTableHeader}>
                {t("myTeamTable.player", "Player")}
              </div>
            </th>

            <OverlayHeaderCells columns={columns} />
          </tr>
        </thead>

        {Object.keys(positionsByElementType).map((et) => {
          const positions = positionsByElementType[et];
          const positionPicks = playersByElementType[et] || [];

          // Skip if no players in this position
          if (positionPicks.length === 0) {
            return null;
          }

          return (
            <tbody className={listTableBody} key={et}>
              <tr>
                <th
                  scope="rowgroup"
                  className={listTablePositionCell}
                  colSpan={columns.length + 1}
                >
                  <div className={listTablePosition}>
                    {elementTypesByPosition[positions[0]].thisType.plural_name}
                  </div>
                </th>
              </tr>
              {positionPicks.map((p) => (
                <MyTeamTableRow
                  key={p.element}
                  pick={p}
                  chipInPlayName={chipInPlayName}
                  element={elementsById[p.element]}
                  teamsById={teamsById}
                  handleElementClick={handleElementClick}
                  renderDreamTeam={renderDreamTeam}
                  overlay={selectedOverlay}
                  stats={stats}
                />
              ))}
            </tbody>
          );
        })}

        {benchPlayers.length > 0 && (
          <tbody className={listTableBody}>
            <tr>
              <th
                scope="rowgroup"
                className={listTablePositionCell}
                colSpan={columns.length + 1}
              >
                <div className={listTablePosition}>
                  {t("myTeamTable.substitutes", "Substitutes")}
                </div>
              </th>
            </tr>
            {benchPlayers.map((p) => (
              <MyTeamTableRow
                key={p.element}
                pick={p}
                chipInPlayName={chipInPlayName}
                element={elementsById[p.element]}
                teamsById={teamsById}
                handleElementClick={handleElementClick}
                renderDreamTeam={renderDreamTeam}
                overlay={selectedOverlay}
                stats={stats}
              />
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
};
export default MyTeamTable;
